import {
    Flex,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Popover,
    PopoverAnchor,
    PopoverBody,
    PopoverContent,
    Portal,
    Stack,
    Text,
    useDisclosure,
    useOutsideClick,
} from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { MoneyCell } from '../widgets/Table/MoneyCell';
import { ManualTrade, ManualTradePrice, PriceType } from './TradeBlotter';
import { tradeCalculator } from './tradeCalculator';

const PRICE_OPTIONS: { type: PriceType; percentage?: number; highlight?: string }[] = [
    { type: 'ASK', percentage: 2 },
    { type: 'ASK', percentage: 1.5 },
    { type: 'ASK', percentage: 1 },
    { type: 'ASK', percentage: 0.5 },
    { type: 'ASK', percentage: 0.25 },
    { type: 'ASK', highlight: 'red' },
    { type: 'ASK', percentage: -0.25 },
    { type: 'ASK', percentage: -0.5 },
    { type: 'ASK', percentage: -1 },
    { type: 'ASK', percentage: -1.5 },
    { type: 'ASK', percentage: -2 },
    { type: 'MID', highlight: 'gray' },
    { type: 'BID', percentage: 2 },
    { type: 'BID', percentage: 1.5 },
    { type: 'BID', percentage: 1 },
    { type: 'BID', percentage: 0.5 },
    { type: 'BID', percentage: 0.25 },
    { type: 'BID', highlight: 'blue' },
    { type: 'BID', percentage: -0.25 },
    { type: 'BID', percentage: -0.5 },
    { type: 'BID', percentage: -1 },
    { type: 'BID', percentage: -1.5 },
    { type: 'BID', percentage: -2 },
];

export const PriceSelector = ({
    clearManualPrice,
    onChange,
    trade,
    fractionDigits = 4,
}: {
    clearManualPrice: () => void;
    onChange: (price: ManualTradePrice) => void;
    trade: ManualTrade;
    fractionDigits?: number;
}) => {
    const ref = useRef<HTMLDivElement | null>(null);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const value = useMemo(
        () => tradeCalculator.getAdjustedCleanPrice(trade)?.toFixed(fractionDigits),
        [fractionDigits, trade],
    );

    const [manualValue, setManualValue] = useState<string>('');

    const closeDropdown = useCallback(() => {
        onClose();
        setManualValue('');
    }, [onClose]);

    useOutsideClick({
        ref: ref,
        handler: (ev) => {
            if (!isOpen) {
                return;
            }

            ev.stopPropagation();

            if (!manualValue && trade.price?.type === 'MANUAL') {
                clearManualPrice();
            }

            if (manualValue && Number(manualValue).toFixed(fractionDigits) !== Number(value).toFixed(fractionDigits)) {
                onChange({
                    type: 'MANUAL',
                    value: Number(manualValue),
                });
            }

            closeDropdown();
        },
    });

    return (
        <Popover autoFocus={false} isOpen={isOpen} onClose={closeDropdown} isLazy>
            <PopoverAnchor>
                <div ref={ref}>
                    <NumberInput
                        size="sm"
                        value={isOpen ? manualValue : value}
                        onChange={(val) => {
                            setManualValue(val);
                        }}
                        isDisabled={trade.orderType === 'MKT'}
                        onFocus={onOpen}
                        autoFocus={false}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </div>
            </PopoverAnchor>
            <Portal>
                <PopoverContent
                    maxHeight="340px"
                    width="fit-content"
                    overflowY="auto"
                    fontSize="sm"
                    boxShadow="dark-lg"
                >
                    <PopoverBody>
                        <Stack gap={0}>
                            {PRICE_OPTIONS.map(({ type, percentage, highlight }, idx) => {
                                const val = tradeCalculator.getAdjustedCleanPrice({
                                    ...trade,
                                    price: { type, percentage },
                                });
                                return (
                                    <Flex
                                        key={idx}
                                        p={2}
                                        _hover={{ background: 'gray.600', color: 'white' }}
                                        cursor="pointer"
                                        borderRadius="sm"
                                        onClick={(ev) => {
                                            ev.stopPropagation();

                                            onChange({
                                                type: type,
                                                percentage,
                                            });
                                            closeDropdown();
                                        }}
                                        backgroundColor={highlight && `${highlight}.200`}
                                        color={highlight && `${highlight}.700`}
                                        fontWeight={highlight && 'medium'}
                                    >
                                        <Text as="span" w="40px">
                                            {type}
                                        </Text>
                                        <Text as="span" w="56px" textAlign="center" mr={5}>
                                            {percentage && percentage > 0 && '+'}
                                            {percentage}
                                            {percentage && '%'}
                                        </Text>
                                        <MoneyCell value={val} fractionDigits={4} />
                                    </Flex>
                                );
                            })}
                        </Stack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};
